import React, { useCallback, useEffect, useState } from "react";
import { ReactSVG } from "react-svg";

// assets
import LogotipoTopconTheConcreteSuite from "@assets/company/LogotipoTopconTheConcreteSuite.svg";
import {
  certificate,
  client,
  concreting,
  concretingApproval,
  concretingRequest,
  contract,
  invoice,
  permissions
} from "@assets/applications/index";

// atoms
import { DashboardMenuItem } from "@atoms/index";

import type { MenuItem } from "@molecules/index";
// molecules
import { LgpdDialog, WidgetLastConcretings } from "@molecules/index";

// Language
import useTranslation from "@languages/useTranslation";
import { CustomerMessages } from "src/languages/interfaces";

/** hooks */
import { useAuth } from "@hooks/useAuth";
import { usePermissions } from "@pages/Office";

import * as Styled from "./styles";

const DashboardMobile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { formattedPermissions } = usePermissions();

  const [menuItems, setMenuItems] = useState<MenuItem[]>(() => [
    {
      id: "1",
      icon: concretingRequest,
      title: t(CustomerMessages.applicationConcretingRequest),
      link: "concreting-requests",
      hidden: true
    },
    {
      id: "2",
      icon: concreting,
      title: t(CustomerMessages.applicationConcreting),
      link: "concretings",
      hidden: true
    },
    {
      id: "3",
      icon: contract,
      title: t(CustomerMessages.applicationContract),
      link: "contracts",
      hidden: true
    },
    {
      id: "4",
      icon: invoice,
      title: t(CustomerMessages.applicationInvoice),
      link: "invoices",
      hidden: true
    },
    {
      id: "5",
      icon: concretingApproval,
      title: t(CustomerMessages.applicationConcretingApproval),
      link: "concreting-approvals",
      onlyAdmin: true
    },
    {
      id: "6",
      icon: certificate,
      title: t(CustomerMessages.applicationCertificate),
      link: "certificates",
      hidden: true
    },
    {
      id: "7",
      icon: client,
      title: t(CustomerMessages.applicationClient),
      link: "clients",
      hidden: true
    },
    {
      id: "8",
      icon: permissions,
      title: t(CustomerMessages.applicationPermission),
      link: "permissions",
      onlyAdmin: true
    }
  ]);

  const updateMenuMobile = useCallback(
    (permissions: Record<string, { modules: (string | null)[] }>) => {
      const allowedModules = new Set(
        Object.values(permissions)
          .flatMap((perm) => perm.modules)
          .filter((mod): mod is string => mod !== null)
      );

      setMenuItems((prevMenuItems) => prevMenuItems.map((item) => ({
        ...item,
        hidden: !allowedModules.has(`/${item.link}`)
      })));

    },
    []
  );

  useEffect(() => {
    if (!user.admin && formattedPermissions) {
      updateMenuMobile(formattedPermissions);
    }
  }, [user, formattedPermissions, updateMenuMobile]);

  return (
    <>
      <Styled.Container>
        <Styled.DashboardContainer container spacing={2}>
          <Styled.WidgetContainer item xs={12} md={6}>
            <WidgetLastConcretings />
          </Styled.WidgetContainer>

          <Styled.DashboardIconsContainer item xs={12} md={6}>
            <Styled.DashboardIconsContent container spacing={2}>
              {menuItems.map(({ id, icon, title, link, onlyAdmin, hidden }) => {
                if (onlyAdmin && !user.admin) return null;
                if (!user.admin && hidden) return null;

                return (
                  <Styled.DashboardIconsItem key={id} item xs={6}>
                    <DashboardMenuItem icon={icon} title={title} link={link} />
                  </Styled.DashboardIconsItem>
                );
              })}
            </Styled.DashboardIconsContent>
          </Styled.DashboardIconsContainer>
        </Styled.DashboardContainer>

        <Styled.LogotipoContainer>
          <ReactSVG src={LogotipoTopconTheConcreteSuite} />
        </Styled.LogotipoContainer>
      </Styled.Container>
      <LgpdDialog />
    </>
  );
};

export default DashboardMobile;
